import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams } from 'react-router';
import { ToastHost } from './components/ui';
import { Splash } from './app/Splash';
import { useSession } from './state/session';
import { OnboardingPage } from './app/pages/OnboardingPage';
import { AtcPlayback } from './features/conditions/AtcPlayback';
import { LoginPage } from './app/pages/LoginPage';
import { MissionsPage } from './app/pages/MissionsPage';
import { NewMissionPage } from './app/pages/NewMissionPage';
import { JoinPage } from './app/pages/JoinPage';
import { NotFoundPage } from './app/pages/NotFoundPage';
import { MissionShell, MissionIndexRedirect } from './app/MissionShell';
import { AirportCamerasPage } from './app/pages/AirportCamerasPage';
import { LegalPage } from './app/pages/LegalPage';
import { TermsPage } from './app/pages/TermsPage';
import { CreditsPage } from './app/pages/CreditsPage';
import { OrganizationPage } from './app/pages/OrganizationPage';
import { SettingsPage } from './app/pages/SettingsPage';
import { RouteError } from './app/RouteError';
import { reloadIfStale } from './lib/buildCheck';
import { usePlan } from './state/plan';
import { SuspendedScreen } from './features/moderation/SuspendedScreen';
const AircraftPage = lazy(() => import('./features/aircraft').then((m) => ({ default: m.AircraftPage })));
const AircraftDetailPage = lazy(() => import('./features/aircraft').then((m) => ({ default: m.AircraftDetailPage })));
const FlightReplayPage = lazy(() => import('./features/aircraft').then((m) => ({ default: m.FlightReplayPage })));

// Feature pages are code-split; MapLibre only loads with a map page.
const LivePage = lazy(() => import('./features/live').then((m) => ({ default: m.LivePage })));
const PlanPage = lazy(() => import('./features/plan').then((m) => ({ default: m.PlanPage })));
const ReplayPage = lazy(() => import('./features/replay').then((m) => ({ default: m.ReplayPage })));
const TimecodePage = lazy(() => import('./features/timecode').then((m) => ({ default: m.TimecodePage })));
const AirToAirPage = lazy(() => import('./features/a2a').then((m) => ({ default: m.AirToAirPage })));
const MapBench = lazy(() => import('./dev/MapBench').then((m) => ({ default: m.MapBench })));
const MediaPage = lazy(() => import('./features/media').then((m) => ({ default: m.MediaPage })));
const PointPage = lazy(() => import('./features/point').then((m) => ({ default: m.PointPage })));
const AlertsPage = lazy(() => import('./features/alertspage').then((m) => ({ default: m.AlertsPage })));
// three.js only loads with the Shot Planner.
const ShotPlannerPage = lazy(() => import('./app/pages/ShotPlannerPage').then((m) => ({ default: m.ShotPlannerPage })));
const TeamPage = lazy(() => import('./features/team').then((m) => ({ default: m.TeamPage })));

function AircraftRedirect() {
  const { id } = useParams();
  const loc = useLocation();
  return <Navigate to={`/fleets/${id}${loc.search}`} replace />;
}

/**
 * A suspended account (403 account_suspended at sign-in or mid-session, WebSocket close 4423) sees
 * only the suspension screen; the public support, privacy and credits pages stay reachable.
 */
function SuspensionGate() {
  const status = useSession((s) => s.status);
  const notice = useSession((s) => s.suspension);
  if (status === 'suspended' && notice) return <SuspendedScreen notice={notice} />;
  return <Outlet />;
}

function RequireAuth({ children }: { children?: ReactNode }) {
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);
  const loc = useLocation();
  useEffect(() => {
    if (status === 'unknown') void load();
  }, [status, load]);
  if (status === 'unknown') return <Splash />;
  if (status === 'offline') return <Splash message="Can't reach Airview Studio. Check your connection and retry." retry={load} />;
  if (status === 'anonymous') return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children ?? <Outlet />}</>;
}

const page = (el: ReactNode) => <Suspense fallback={<Splash inline />}>{el}</Suspense>;

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
  { path: '/privacy', element: <LegalPage /> },
  { path: '/support', element: <LegalPage support /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/credits', element: <CreditsPage /> },
  {
  element: <SuspensionGate />,
  children: [
  { path: '/', element: <Navigate to="/missions" replace /> },
  { path: '/login', element: <LoginPage mode="login" /> },
  { path: '/register', element: <LoginPage mode="register" /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/fleets', element: page(<AircraftPage />) },
      { path: '/fleets/:id', element: page(<AircraftDetailPage />) },
      { path: '/fleets/:id/flights/:start', element: page(<FlightReplayPage />) },
      { path: '/aircraft', element: <Navigate to="/fleets" replace /> },
      { path: '/aircraft/:id', element: <AircraftRedirect /> },
      { path: '/cameras', element: <AirportCamerasPage /> },
      { path: '/shot-planner', element: page(<ShotPlannerPage />) },
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/organization', element: <OrganizationPage /> },
      { path: '/missions', element: <MissionsPage /> },
      { path: '/missions/new', element: <NewMissionPage /> },
      { path: '/join/:code?', element: <JoinPage /> },
      {
        path: '/m/:id',
        element: <MissionShell />,
        children: [
          { index: true, element: <MissionIndexRedirect /> },
          { path: 'plan', element: page(<PlanPage />) },
          { path: 'production', element: <Navigate to="../plan?view=production" replace /> },
          { path: 'live', element: page(<LivePage />) },
          { path: 'replay', element: page(<ReplayPage />) },
          { path: 'timecode', element: page(<TimecodePage />) },
          { path: 'a2a', element: page(<AirToAirPage />) },
          { path: 'media', element: page(<MediaPage />) },
          { path: 'point', element: page(<PointPage />) },
          { path: 'alerts', element: page(<AlertsPage />) },
          { path: 'team', element: page(<TeamPage />) },
        ],
      },
    ],
  },
  ],
  },
  ...(import.meta.env.DEV ? [{ path: '/dev/map-bench', element: page(<MapBench />) }] : []),
  { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

// Back in the foreground after a while (phones keep tabs for days): pick up a new build if one is
// live, unless a plan edit is still waiting to save.
if (typeof document !== 'undefined') {
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt = Date.now();
    else if (Date.now() - hiddenAt > 60_000 && usePlan.getState().save === 'saved') void reloadIfStale();
  });
}

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <AtcPlayback />
      <ToastHost />
    </>
  );
}
